import React from "react";
import { formatDecimal } from "../../utils/numberFormat";

// The "Inventory / Parts Used" card, drawn once for both the new-booking picker
// (StagedPartsSection) and the saved-booking one (PartsUsedSection): a titled card with the running
// total, the add bar (passed in as children), and the parts in the application's standard grid.
//
//   rows   [{ id, name, quantity, unit, unitPrice, lineTotal, onRemove? }]   (no onRemove = nothing to click)
//   footer optional node under the grid (e.g. parts tax)
const thStyle = {
  background: "#F9FAFB",
  fontWeight: 600,
  fontSize: 13,
  color: "#374151",
  padding: "10px 16px",
  borderBottom: "1px solid #E5E7EB",
  textAlign: "left",
  whiteSpace: "nowrap"
};
const tdStyle = {
  padding: "11px 16px",
  fontSize: 14,
  color: "#374151",
  borderBottom: "1px solid #F3F4F6",
  verticalAlign: "middle"
};
const num = { textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };

const removeStyle = {
  width: 24,
  height: 24,
  padding: 0,
  border: 0,
  borderRadius: "50%",
  background: "#FDEDF1",
  color: "#C0335A",
  fontSize: 11,
  lineHeight: 1,
  cursor: "pointer"
};

export default function PartsPanel({ title = "Inventory / Parts Used", currency = "", total = 0, children, rows, loading, emptyText, footer }) {
  const hover = (on) => (e) => {
    e.currentTarget.style.background = on ? "#F9FAFB" : "transparent";
  };
  return (
    <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }} className="mt-3" data-testid="parts-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #F3F4F6" }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{title}</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: "#111827", whiteSpace: "nowrap" }}>
          {currency} {Number(total).toFixed(2)}
        </span>
      </div>

      <div style={{ padding: "12px 20px", background: "#FCFCFD", borderBottom: "1px solid #F3F4F6" }}>{children}</div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 20, color: "#6B7280", fontSize: 13 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: "center", padding: 24, color: "#6B7280", fontSize: 13 }}>{emptyText}</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Item</th>
                <th style={{ ...thStyle, ...num }}>Qty</th>
                <th style={{ ...thStyle, ...num }}>Unit price</th>
                <th style={{ ...thStyle, ...num }}>Line total</th>
                <th style={{ ...thStyle, width: 48 }} aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onMouseEnter={hover(true)} onMouseLeave={hover(false)} style={{ transition: "background 0.15s" }}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: "#111827" }}>{r.name}</td>
                  <td style={{ ...tdStyle, ...num }}>
                    {formatDecimal(r.quantity)} {r.unit}
                  </td>
                  <td style={{ ...tdStyle, ...num }}>
                    {currency} {formatDecimal(r.unitPrice)}
                  </td>
                  <td style={{ ...tdStyle, ...num, fontWeight: 600, color: "#111827" }}>
                    {currency} {Number(r.lineTotal).toFixed(2)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    {r.onRemove && (
                      <button type="button" style={removeStyle} aria-label={`Remove ${r.name}`} onClick={r.onRemove}>
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {footer}
    </div>
  );
}
